import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { CanvasRenderer, type RenderableElement } from "./CanvasRenderer";
import { compactContentRows } from "./contentHeight";

it("passes the original authored element to a runtime grid renderer", () => {
  const grid = { ...element("grid", { auto_height: true }), type: "view_grid" };
  const renderViewGrid = jest.fn(() => <div>Rows</div>);
  render(
    <CanvasRenderer
      elements={[grid]}
      config={{ columns: 12, rowHeight: 60, gap: 8, minRows: 4 }}
      mode="runtime"
      renderViewGrid={renderViewGrid}
    />,
  );
  expect(renderViewGrid).toHaveBeenCalledWith(grid);
});

function element(
  id: string,
  props: Record<string, unknown>,
  parent?: string,
  row = 1,
  span = 20,
): RenderableElement {
  return {
    id,
    type: "container",
    props: props as never,
    parentElementId: parent,
    gridColumn: 1,
    gridRow: row,
    columnSpan: 12,
    rowSpan: span,
  };
}

it("contracts authored empty bands and propagates natural height through tabs, cards and an embedded child canvas", () => {
  const root = element("tabs", {
    containerType: "TABS",
    auto_height: true,
    panels: [
      { id: "one", label: "Profile" },
      { id: "two", label: "Tasks" },
    ],
  });
  const card = {
    ...element("card", { containerType: "CARD", title: "Personal" }, "tabs"),
    parentPanelId: "one",
  };
  const embed = { ...element("embed", {}, "card", 5), type: "embedded_canvas" };
  const tasks = {
    ...element("tasks", { containerType: "SECTION" }, "tabs"),
    parentPanelId: "two",
  };
  const config = { columns: 12, rowHeight: 60, gap: 8, minRows: 4 };
  render(
    <CanvasRenderer
      elements={[root, card, embed, tasks]}
      config={config}
      mode="runtime"
      renderEmbeddedCanvas={() => (
        <CanvasRenderer
          elements={[
            { ...element("child", { label: "Child form" }), type: "text" },
          ]}
          config={config}
          mode="runtime"
        />
      )}
    />,
  );
  expect(screen.getByTestId("canvas-element-tabs")).toHaveStyle({
    gridRow: "1/span 1",
  });
  const child = screen.getByTestId("canvas-element-child");
  expect(child).toHaveStyle({ gridRow: "1/span 1", alignSelf: "start" });
  expect(screen.getAllByTestId("canvas-root-grid")[1]).toHaveStyle({
    gridAutoRows: "minmax(0, auto)",
  });
  fireEvent.click(screen.getByRole("tab", { name: "Tasks" }));
  expect(screen.getByRole("tab", { name: "Tasks" })).toHaveAttribute(
    "aria-selected",
    "true",
  );
});

it("keeps fixed authored heights unless the containing scope opts into auto height", () => {
  render(
    <CanvasRenderer
      elements={[element("fixed", { containerType: "SECTION" })]}
      config={{ columns: 12, rowHeight: 60, gap: 8, minRows: 4 }}
      mode="runtime"
    />,
  );
  expect(screen.getByTestId("canvas-element-fixed")).toHaveStyle({
    gridRow: "1/span 20",
  });
  expect(screen.getByTestId("canvas-root-grid")).toHaveStyle({
    gridAutoRows: "60px",
  });
});

it("an explicit fixed-height child stops inheritance into its embedded canvas", () => {
  const config = { columns: 12, rowHeight: 60, gap: 8, minRows: 4 };
  render(
    <CanvasRenderer
      elements={[
        {
          ...element("scope", { containerType: "SECTION", auto_height: true }),
        },
        {
          ...element("fixed-child", { auto_height: false }, "scope"),
          type: "embedded_canvas",
        },
      ]}
      config={config}
      mode="runtime"
      renderEmbeddedCanvas={() => (
        <CanvasRenderer
          elements={[
            { ...element("child", { label: "Fixed child" }), type: "text" },
          ]}
          config={config}
          mode="runtime"
        />
      )}
    />,
  );
  expect(screen.getByTestId("canvas-element-fixed-child")).toHaveStyle({
    minHeight: "1352px",
  });
  expect(screen.getAllByTestId("canvas-root-grid")[1]).toHaveStyle({
    gridAutoRows: "60px",
  });
});

it("keeps overlapping row relationships and columns while removing unused row bands", () => {
  const items = [
    { id: "card", gridRow: 1, rowSpan: 20, gridColumn: 1 },
    { id: "heading", gridRow: 1, rowSpan: 1, gridColumn: 7 },
    { id: "detail", gridRow: 4, rowSpan: 16, gridColumn: 7 },
    { id: "next", gridRow: 30, rowSpan: 5, gridColumn: 1 },
  ];
  const compact = compactContentRows(items);
  expect(
    compact.map((item) => [
      item.id,
      item.gridRow,
      item.rowSpan,
      item.gridColumn,
    ]),
  ).toEqual([
    ["card", 1, 4, 1],
    ["heading", 1, 1, 7],
    ["detail", 3, 1, 7],
    ["next", 5, 1, 1],
  ]);
  expect(items[0].rowSpan).toBe(20);
});
